import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { safeNumber } from "../General/timeFormatter";

const INITIALLY_DESELECTED = {
  alerts: new Set(["80"]),
  lines: new Set([]),
  recipes: new Set([]),
  objects: new Set([]),
};

export const useCipFilters = (filteredData) => {
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [selectedLines, setSelectedLines] = useState([]);
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  const [selectedAlertTypes, setSelectedAlertTypes] = useState([]);
  const [searchFilters, setSearchFilters] = useState({
    objects: "",
    recipes: "",
  });

  const didInit = useRef(false);

  // Get unique values
  const uniqueObjects = useMemo(
    () =>
      [
        ...new Set(filteredData.map((row) => row.object_name).filter(Boolean)),
      ].sort(),
    [filteredData]
  );

  const uniqueLines = useMemo(
    () =>
      [...new Set(filteredData.map((row) => row.line).filter(Boolean))].sort(),
    [filteredData]
  );

  const uniqueRecipes = useMemo(
    () =>
      [
        ...new Set(filteredData.map((row) => row.recipe_name).filter(Boolean)),
      ].sort(),
    [filteredData]
  );

  const uniqueAlertTypes = useMemo(
    () =>
      [
        ...new Set(filteredData.map((row) => row.alert_id).filter(Boolean)),
      ].sort((a, b) => safeNumber(a, 0) - safeNumber(b, 0)),
    [filteredData]
  );

  // Filtered lists based on search
  const filteredObjects = useMemo(
    () =>
      uniqueObjects.filter((obj) =>
        obj.toLowerCase().includes(searchFilters.objects.toLowerCase())
      ),
    [uniqueObjects, searchFilters.objects]
  );

  const filteredRecipes = useMemo(
    () =>
      uniqueRecipes.filter((recipe) =>
        recipe.toLowerCase().includes(searchFilters.recipes.toLowerCase())
      ),
    [uniqueRecipes, searchFilters.recipes]
  );

  // Initialize selections
  useEffect(() => {
    if (!didInit.current) {
      const haveOptions =
        uniqueAlertTypes.length ||
        uniqueLines.length ||
        uniqueRecipes.length ||
        uniqueObjects.length;
      if (!haveOptions) return;

      setSelectedAlertTypes(
        uniqueAlertTypes.filter(
          (id) => !INITIALLY_DESELECTED.alerts.has(String(id))
        )
      );
      setSelectedLines(uniqueLines);
      setSelectedRecipes(uniqueRecipes);
      setSelectedObjects(uniqueObjects);

      didInit.current = true;
      return;
    }

    // Prune invalid selections
    const alertTypesStr = new Set(uniqueAlertTypes.map(String));
    setSelectedAlertTypes((prev) =>
      prev.filter((v) => alertTypesStr.has(String(v)))
    );
    setSelectedLines((prev) => prev.filter((v) => uniqueLines.includes(v)));
    setSelectedRecipes((prev) => prev.filter((v) => uniqueRecipes.includes(v)));
    setSelectedObjects((prev) => prev.filter((v) => uniqueObjects.includes(v)));
  }, [uniqueAlertTypes, uniqueLines, uniqueRecipes, uniqueObjects]);

  // Helper functions
  const updateSearchFilter = useCallback((filterType, value) => {
    setSearchFilters((prev) => ({ ...prev, [filterType]: value }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setSelectedObjects([]);
    setSelectedLines([]);
    setSelectedRecipes([]);
    setSelectedAlertTypes(uniqueAlertTypes);
    setSearchFilters({ objects: "", recipes: "" });
  }, [uniqueAlertTypes]);

  return {
    // State
    selectedObjects,
    setSelectedObjects,
    selectedLines,
    setSelectedLines,
    selectedRecipes,
    setSelectedRecipes,
    selectedAlertTypes,
    setSelectedAlertTypes,
    searchFilters,

    // Derived data
    uniqueObjects,
    uniqueLines,
    uniqueRecipes,
    uniqueAlertTypes,
    filteredObjects,
    filteredRecipes,

    // Functions
    updateSearchFilter,
    clearAllFilters,
  };
};
